import { Ionicons } from '@expo/vector-icons';
import { Asset } from 'expo-asset';
import * as LegacyFS from 'expo-file-system/legacy';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  I18nManager,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { useTheme } from '../contexts/ThemeContext';
import reportGenerator from '../services/reportGenerator';
import logger from '../utils/logger';
import { useHeaderHeight } from '@react-navigation/elements';

interface ReportOptionsScreenProps {
  navigation: any;
  route: {
    params: {
      device: {
        id: string;
        name: string;
        deviceType: string;
        site?: string;
        zone?: string;
        hasBattery?: boolean;
      };
      telemetryData: any[];
      organizationName: string;
      timeRange?: string;
    };
  };
}

export default function ReportOptionsScreen({ navigation, route }: ReportOptionsScreenProps) {
  // Header is transparent (see stack navigator) — push content below it
  const headerHeight = useHeaderHeight();
  const { device, telemetryData, organizationName, timeRange } = route.params;
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const [generating, setGenerating] = useState(false);
  const [logoBase64, setLogoBase64] = useState<string>('');
  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;

  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? 'خيارات التقرير' : 'Report Options',
      headerTintColor: '#ffffff',
    });
  }, [isArabic]);

  // Load platform logo for PDF reports
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const asset = Asset.fromModule(require('../../assets/logo.png'));
        await asset.downloadAsync();
        if (asset.localUri) {
          const b64 = await LegacyFS.readAsStringAsync(asset.localUri, {
            encoding: LegacyFS.EncodingType.Base64,
          });
          setLogoBase64(b64);
        }
      } catch (e) {
        logger.warn('Could not load logo for report:', e);
      }
    };
    loadLogo();
  }, []);

  // Report options
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeStatistics, setIncludeStatistics] = useState(true);
  const [includeRawData, setIncludeRawData] = useState(true);
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'csv'>('pdf');

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);

      const dateRangeLabel =
        timeRange === '30d'
          ? isArabic
            ? '\u0622\u062e\u0631 30 \u064a\u0648\u0645'
            : 'Last 30 Days'
          : timeRange === 'custom'
          ? isArabic
            ? '\u0646\u0637\u0627\u0642 \u0645\u062e\u0635\u0635'
            : 'Custom Range'
          : isArabic
          ? '\u0622\u062e\u0631 24 \u0633\u0627\u0639\u0629'
          : 'Last 24 Hours';

      const options = {
        includeCharts,
        includeStatistics,
        includeRawData,
        dateRange: dateRangeLabel,
      };

      if (selectedFormat === 'pdf') {
        // Generate PDF
        const uri = await reportGenerator.generateDeviceReport(
          { ...device, deviceType: device.deviceType as any },
          telemetryData,
          options,
          organizationName,
          isArabic ? 'ar' : 'en',
          logoBase64 || undefined,
        );

        // Share PDF
        await reportGenerator.shareReport(
          uri,
          `${device.name}_Report_${new Date().toISOString().split('T')[0]}.pdf`,
        );
      } else {
        // Export CSV
        const uri = await reportGenerator.exportToCSV(
          { ...device, deviceType: device.deviceType as any },
          telemetryData,
        );
        await reportGenerator.shareReport(
          uri,
          `${device.name}_Data_${new Date().toISOString().split('T')[0]}.csv`,
        );
      }

      Alert.alert(
        isArabic ? 'نجح' : 'Success',
        isArabic ? 'تم إنشاء التقرير بنجاح' : 'Report generated successfully',
      );
    } catch (error) {
      logger.error('Failed to generate report:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'فشل إنشاء التقرير' : 'Failed to generate report',
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <View
      style={[
        styles.container,
        { backgroundColor: theme.colors.background, paddingTop: headerHeight },
      ]}
    >
      <ScrollView>
        <View style={styles.content}>
          {/* Device Info Preview */}
          <View
            style={[
              styles.section,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
          >
            <View style={[styles.devicePreview, { flexDirection: 'row' }]}>
              <View style={[styles.deviceIcon, { backgroundColor: theme.colors.primary + '20' }]}>
                <Ionicons name="document-text" size={32} color={theme.colors.primary} />
              </View>
              <View style={styles.deviceInfo}>
                <Text
                  style={[
                    styles.deviceName,
                    { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                  ]}
                >
                  {device.name}
                </Text>
                <Text
                  style={[
                    styles.deviceMeta,
                    { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                  ]}
                >
                  {device.deviceType}
                </Text>
                <Text
                  style={[
                    styles.deviceMeta,
                    { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                  ]}
                >
                  {telemetryData.length} {isArabic ? 'قراءة' : 'readings'}
                </Text>
              </View>
            </View>
          </View>

          {/* Format Selection */}
          <View
            style={[
              styles.section,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
          >
            <Text
              style={[
                styles.sectionTitle,
                { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
              ]}
            >
              {isArabic ? 'تنسيق التقرير' : 'Report Format'}
            </Text>
            <View style={[styles.formatButtons, { flexDirection: 'row' }]}>
              <TouchableOpacity
                style={[
                  styles.formatButton,
                  { borderColor: theme.colors.border },
                  selectedFormat === 'pdf' && {
                    backgroundColor: '#3949ab',
                    borderColor: theme.colors.primary,
                  },
                ]}
                onPress={() => setSelectedFormat('pdf')}
              >
                <Ionicons
                  name="document"
                  size={24}
                  color={selectedFormat === 'pdf' ? '#FFF' : theme.colors.text}
                />
                <Text
                  style={[
                    styles.formatButtonText,
                    { color: theme.colors.text },
                    selectedFormat === 'pdf' && { color: '#FFF' },
                  ]}
                >
                  PDF
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  styles.formatButton,
                  { borderColor: theme.colors.border },
                  selectedFormat === 'csv' && {
                    backgroundColor: '#3949ab',
                    borderColor: theme.colors.primary,
                  },
                ]}
                onPress={() => setSelectedFormat('csv')}
              >
                <Ionicons
                  name="grid"
                  size={24}
                  color={selectedFormat === 'csv' ? '#FFF' : theme.colors.text}
                />
                <Text
                  style={[
                    styles.formatButtonText,
                    { color: theme.colors.text },
                    selectedFormat === 'csv' && { color: '#FFF' },
                  ]}
                >
                  CSV
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* PDF Options */}
          {selectedFormat === 'pdf' && (
            <View
              style={[
                styles.section,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
            >
              <Text
                style={[
                  styles.sectionTitle,
                  { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                ]}
              >
                {isArabic ? 'خيارات التقرير' : 'Report Options'}
              </Text>

              <View style={[styles.option, { flexDirection: 'row' }]}>
                <View style={[styles.optionInfo, { flexDirection: 'row' }]}>
                  <Ionicons name="bar-chart" size={24} color={theme.colors.primary} />
                  <View style={styles.optionText}>
                    <Text
                      style={[
                        styles.optionLabel,
                        { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                      ]}
                    >
                      {isArabic ? 'تضمين الرسوم البيانية' : 'Include Charts'}
                    </Text>
                    <Text
                      style={[
                        styles.optionDescription,
                        { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                      ]}
                    >
                      {isArabic ? 'عرض البيانات بصرياً' : 'Visual data representation'}
                    </Text>
                  </View>
                </View>
                <Switch
                  value={includeCharts}
                  onValueChange={setIncludeCharts}
                  trackColor={{ false: theme.colors.border, true: theme.colors.primary + '80' }}
                  thumbColor={includeCharts ? theme.colors.primary : theme.colors.textSecondary}
                />
              </View>

              <View style={[styles.option, { flexDirection: 'row' }]}>
                <View style={[styles.optionInfo, { flexDirection: 'row' }]}>
                  <Ionicons name="stats-chart" size={24} color={theme.colors.info} />
                  <View style={styles.optionText}>
                    <Text
                      style={[
                        styles.optionLabel,
                        { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                      ]}
                    >
                      {isArabic ? 'تضمين الإحصائيات' : 'Include Statistics'}
                    </Text>
                    <Text
                      style={[
                        styles.optionDescription,
                        { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                      ]}
                    >
                      {isArabic ? 'الحد الأدنى، الأعلى، المتوسط' : 'Min, Max, Average values'}
                    </Text>
                  </View>
                </View>
                <Switch
                  value={includeStatistics}
                  onValueChange={setIncludeStatistics}
                  trackColor={{ false: theme.colors.border, true: theme.colors.primary + '80' }}
                  thumbColor={includeStatistics ? theme.colors.primary : theme.colors.textSecondary}
                />
              </View>

              <View style={[styles.option, { flexDirection: 'row' }]}>
                <View style={[styles.optionInfo, { flexDirection: 'row' }]}>
                  <Ionicons name="list" size={24} color={theme.colors.secondary} />
                  <View style={styles.optionText}>
                    <Text
                      style={[
                        styles.optionLabel,
                        { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                      ]}
                    >
                      {isArabic ? 'تضمين البيانات الخام' : 'Include Raw Data'}
                    </Text>
                    <Text
                      style={[
                        styles.optionDescription,
                        { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                      ]}
                    >
                      {isArabic ? 'جدول القراءات الكامل' : 'Full readings table'}
                    </Text>
                  </View>
                </View>
                <Switch
                  value={includeRawData}
                  onValueChange={setIncludeRawData}
                  trackColor={{ false: theme.colors.border, true: theme.colors.primary + '80' }}
                  thumbColor={includeRawData ? theme.colors.primary : theme.colors.textSecondary}
                />
              </View>
            </View>
          )}

          {/* CSV Info */}
          {selectedFormat === 'csv' && (
            <View
              style={[
                styles.infoBox,
                {
                  backgroundColor: theme.colors.info + '15',
                  borderColor: theme.colors.info,
                  flexDirection: 'row',
                },
              ]}
            >
              <Ionicons name="information-circle" size={24} color={theme.colors.info} />
              <Text
                style={[
                  styles.infoText,
                  { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
                ]}
              >
                {isArabic
                  ? 'سيتم تصدير جميع البيانات بتنسيق CSV لاستخدامها في Excel أو Google Sheets'
                  : 'All data will be exported in CSV format for use in Excel or Google Sheets'}
              </Text>
            </View>
          )}

          {/* Preview Info */}
          <View
            style={[
              styles.previewBox,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
          >
            <Text
              style={[
                styles.previewTitle,
                { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
              ]}
            >
              {isArabic ? 'معاينة التقرير' : 'Report Preview'}
            </Text>
            <View style={styles.previewItems}>
              <View style={[styles.previewItem, { flexDirection: 'row' }]}>
                <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
                <Text style={[styles.previewItemText, { color: theme.colors.text }]}>
                  {isArabic ? 'معلومات الجهاز' : 'Device Information'}
                </Text>
              </View>
              {selectedFormat === 'pdf' && includeStatistics && (
                <View style={[styles.previewItem, { flexDirection: 'row' }]}>
                  <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
                  <Text style={[styles.previewItemText, { color: theme.colors.text }]}>
                    {isArabic ? 'الإحصائيات' : 'Statistics'}
                  </Text>
                </View>
              )}
              {selectedFormat === 'pdf' && includeCharts && (
                <View style={[styles.previewItem, { flexDirection: 'row' }]}>
                  <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
                  <Text style={[styles.previewItemText, { color: theme.colors.text }]}>
                    {isArabic ? 'الرسوم البيانية' : 'Charts'}
                  </Text>
                </View>
              )}
              {selectedFormat === 'pdf' && includeRawData && (
                <View style={[styles.previewItem, { flexDirection: 'row' }]}>
                  <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
                  <Text style={[styles.previewItemText, { color: theme.colors.text }]}>
                    {isArabic ? 'جدول البيانات' : 'Data Table'}
                  </Text>
                </View>
              )}
              {selectedFormat === 'csv' && (
                <View style={[styles.previewItem, { flexDirection: 'row' }]}>
                  <Ionicons name="checkmark-circle" size={20} color={theme.colors.success} />
                  <Text style={[styles.previewItemText, { color: theme.colors.text }]}>
                    {isArabic ? 'تصدير CSV كامل' : 'Full CSV Export'}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Generate Button */}
      <View
        style={[
          styles.footer,
          { backgroundColor: theme.colors.card, borderTopColor: theme.colors.border },
        ]}
      >
        <TouchableOpacity
          style={[styles.generateButton, { backgroundColor: theme.colors.primary }]}
          onPress={handleGenerateReport}
          disabled={generating}
        >
          {generating ? (
            <ActivityIndicator color="#FFF" />
          ) : (
            <>
              <Ionicons name="download-outline" size={20} color="#FFF" />
              <Text style={styles.generateButtonText}>
                {isArabic ? 'إنشاء ومشاركة التقرير' : 'Generate & Share Report'}
              </Text>
            </>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  section: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 16,
  },
  devicePreview: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  deviceIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deviceInfo: {
    flex: 1,
  },
  deviceName: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  deviceMeta: {
    fontSize: 14,
    marginBottom: 2,
  },
  formatButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  formatButton: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    padding: 20,
    borderRadius: 12,
    borderWidth: 2,
    gap: 8,
  },
  formatButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
  },
  optionInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  optionText: {
    flex: 1,
  },
  optionLabel: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 2,
  },
  optionDescription: {
    fontSize: 13,
  },
  infoBox: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
    marginBottom: 16,
    alignItems: 'flex-start',
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  previewBox: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
  },
  previewTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  previewItems: {
    gap: 10,
  },
  previewItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  previewItemText: {
    fontSize: 14,
  },
  footer: {
    padding: 16,
    borderTopWidth: 1,
  },
  generateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  generateButtonText: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '600',
  },
});
